type SupportedLocale = "tr" | "en" | "fr" | "ar" | "ru";

export type ServiceSlug =
  | "kurban"
  | "iftar-sicak-yemek"
  | "su-kuyusu"
  | "gida-dagitimi"
  | "ekmek-dagitimi"
  | "kuran-dagitimi"
  | "elbise-dagitimi"
  | "kirtasiye-dagitimi"
  | "katarakt-ameliyati"
  | "cocuk-sunneti"
  | "mescid-medrese-insasi"
  | "tankerle-su-dagitimi"
  | "un-dagitimi"
  | "sut-kecisi-dagitimi"
  | "battaniye-dagitimi"
  | "cadir-dagitimi";

type CountryId =
  | "chad"
  | "cameroon"
  | "bangladesh"
  | "gaza"
  | "yemen"
  | "afghanistan"
  | "mauritania"
  | "burundi";

interface CountryContent {
  id: CountryId;
  translations: Record<SupportedLocale, {
    name: string;
    summary: string;
  }>;
}

interface ServiceCountryConfig {
  countryId: CountryId;
  videoUrls: string[];
}

interface ServiceLocaleContent {
  name: string;
  heroTitle: string;
  heroSubtitle: string;
  overview: string[];
  highlights: string[];
  videoNote: string;
}

interface ServiceDefinition {
  id: ServiceSlug;
  slug: ServiceSlug;
  countries: ServiceCountryConfig[];
  translations: Record<SupportedLocale, ServiceLocaleContent>;
}

const countryDetails: CountryContent[] = [
  {
    id: "chad",
    translations: {
      tr: {
        name: "Çad",
        summary:
          "Çad'da Sahra ve Sahel hattındaki topluluklarda yerel kanaat önderleriyle birlikte çalışıyor; suya erişim, gıda güvenliği ve temel sağlık projeleri yürütüyoruz."
      },
      en: {
        name: "Chad",
        summary:
          "In Chad we work across Saharan and Sahel communities with local leaders, focusing on water access, food security, and community health."
      },
      fr: {
        name: "Tchad",
        summary:
          "Au Tchad, nous intervenons dans les communautés sahariennes et sahéliennes avec les leaders locaux pour l'accès à l'eau, la sécurité alimentaire et la santé communautaire."
      },
      ru: {
        name: "Чад",
        summary:
          "В Чаде мы работаем в сахаро-сахельских сообществах вместе с местными лидерами, уделяя внимание воде, продовольственной безопасности и здоровью общин."
      },
      ar: {
        name: "تشاد",
        summary:
          "في تشاد نعمل مع قادة المجتمعات الصحراوية والساحلية على مشاريع المياه والأمن الغذائي والصحة المجتمعية."
      }
    }
  },
  {
    id: "cameroon",
    translations: {
      tr: {
        name: "Kamerun",
        summary:
          "Kamerun'da Kousseri kırsalındaki saha ağımızla temiz su, gıda ve acil yardım projelerini güvenilir yerel ortaklarla yürütüyoruz."
      },
      en: {
        name: "Cameroon",
        summary:
          "Around rural Kousseri we rely on trusted local partners to deliver clean water, food, and relief programmes."
      },
      fr: {
        name: "Cameroun",
        summary:
          "Dans la région rurale de Kousseri, nous nous appuyons sur des partenaires fiables pour l'eau potable, l'aide alimentaire et l'urgence."
      },
      ru: {
        name: "Камерун",
        summary:
          "В сельском районе Куссери мы опираемся на надежных местных партнеров для проектов по воде, питанию и экстренной помощи."
      },
      ar: {
        name: "الكاميرون",
        summary:
          "في منطقة كوسيري الريفية نعتمد على شركاء موثوقين لتنفيذ مشاريع المياه والغذاء والإغاثة العاجلة."
      }
    }
  },
  {
    id: "bangladesh",
    translations: {
      tr: {
        name: "Bangladeş (Arakan Kampları)",
        summary:
          "Arakan mülteci kamplarında sıcak yemek, eğitim ve altyapı projelerini kamp yönetimleriyle koordineli yürütüyoruz."
      },
      en: {
        name: "Bangladesh (Rohingya Camps)",
        summary:
          "Within the Rohingya camps we coordinate with camp authorities to deliver hot meals, education and infrastructure support."
      },
      fr: {
        name: "Bangladesh (camps rohingyas)",
        summary:
          "Dans les camps rohingyas nous travaillons avec les autorités du camp pour fournir repas chauds, éducation et soutien aux infrastructures."
      },
      ru: {
        name: "Бангладеш (лагеря рохинджа)",
        summary:
          "В лагерях рохинджа мы координируем работу с администрациями лагерей, обеспечивая горячее питание, образование и поддержку инфраструктуры."
      },
      ar: {
        name: "بنغلاديش (مخيمات الروهينغا)",
        summary:
          "في مخيمات الروهينغا ننسق مع إدارات المخيمات لتقديم الوجبات الساخنة والتعليم ودعم البنية التحتية."
      }
    }
  },
  {
    id: "gaza",
    translations: {
      tr: {
        name: "Gazze",
        summary:
          "Gazze'de yerel ekiplerimizle acil ihtiyaçlara hızlı çözümler üretiyor; un ve su dağıtımlarıyla temel destek sağlıyoruz."
      },
      en: {
        name: "Gaza",
        summary:
          "In Gaza we coordinate with local teams to respond rapidly to critical needs, prioritising flour and water distributions."
      },
      fr: {
        name: "Gaza",
        summary:
          "À Gaza nous travaillons avec nos équipes locales pour répondre rapidement aux besoins vitaux, notamment farine et eau."
      },
      ru: {
        name: "Газа",
        summary:
          "В Газе мы координируем работу с местными командами для быстрой реакции на критические потребности, в том числе муку и воду."
      },
      ar: {
        name: "غزة",
        summary:
          "في غزة نتعاون مع فرقنا المحلية للاستجابة السريعة للاحتياجات العاجلة مع التركيز على توزيع الطحين والمياه."
      }
    }
  },
  {
    id: "yemen",
    translations: {
      tr: {
        name: "Yemen",
        summary:
          "Yemen'de gıda, sağlık ve barınma projelerini yerel saha ortaklarımızla birlikte yürütüyoruz."
      },
      en: {
        name: "Yemen",
        summary:
          "Across Yemen our local partner network helps us deliver food, health and shelter programmes."
      },
      fr: {
        name: "Yémen",
        summary:
          "Au Yémen, notre réseau de partenaires locaux déploie des programmes d'alimentation, de santé et d'abris."
      },
      ru: {
        name: "Йемен",
        summary:
          "В Йемене наша сеть местных партнеров реализует программы по питанию, здравоохранению и жилью."
      },
      ar: {
        name: "اليمن",
        summary:
          "في اليمن ننفذ برامج الغذاء والصحة والإيواء عبر شبكة شركائنا المحليين."
      }
    }
  },
  {
    id: "afghanistan",
    translations: {
      tr: {
        name: "Afganistan",
        summary:
          "Afganistan'ın kuzey vilayetlerinde Türkmen, Özbek ve Tacik topluluklarıyla birlikte çalışıyor; temel ihtiyaçlara yönelik projeler yürütüyoruz."
      },
      en: {
        name: "Afghanistan",
        summary:
          "In northern Afghanistan we collaborate with Turkmen, Uzbek and Tajik communities across multiple provinces to deliver essential support."
      },
      fr: {
        name: "Afghanistan",
        summary:
          "Dans le nord de l'Afghanistan nous coopérons avec les communautés turkmènes, ouzbeks et tadjikes pour répondre aux besoins essentiels."
      },
      ru: {
        name: "Афганистан",
        summary:
          "В северном Афганистане мы сотрудничаем с туркменскими, узбекскими и таджикскими общинами, обеспечивая базовую поддержку."
      },
      ar: {
        name: "أفغانستان",
        summary:
          "في شمال أفغانستان نتعاون مع المجتمعات التركمانية والأوزبكية والطاجيكية في عدة ولايات لتلبية الاحتياجات الأساسية."
      }
    }
  },
  {
    id: "mauritania",
    translations: {
      tr: {
        name: "Moritanya",
        summary:
          "Moritanya'nın kurak bölgelerinde suya erişim, gıda desteği ve toplum dayanıklılığı projelerini yerel ortaklarla yürütüyoruz."
      },
      en: {
        name: "Mauritania",
        summary:
          "Across Mauritania’s arid regions we focus on water access, food support, and community resilience with local partners."
      },
      fr: {
        name: "Mauritanie",
        summary:
          "En Mauritanie, nous agissons dans les zones arides pour l'accès à l'eau, l'aide alimentaire et la résilience communautaire."
      },
      ru: {
        name: "Мавритания",
        summary:
          "В Мавритании мы работаем в засушливых районах над доступом к воде, продовольственной поддержкой и устойчивостью сообществ."
      },
      ar: {
        name: "موريتانيا",
        summary:
          "في موريتانيا نركز في المناطق الجافة على الوصول للمياه والدعم الغذائي وتعزيز صمود المجتمعات بالتعاون مع الشركاء المحليين."
      }
    }
  },
  {
    id: "burundi",
    translations: {
      tr: {
        name: "Burundi",
        summary:
          "Burundi, Bujumbura merkezli genel merkezimiz ve en geniş saha operasyonlarımızın üssüdür; su kuyuları, gıda ve kurban dağıtımları ile medrese takibini burada yürütürüz."
      },
      en: {
        name: "Burundi",
        summary:
          "Burundi is our headquarters in Bujumbura and the base for our largest field operations, including water wells, food and qurban distributions, and madrasa monitoring."
      },
      fr: {
        name: "Burundi",
        summary:
          "Le Burundi est notre siège à Bujumbura et la base de nos opérations majeures, avec puits d'eau, distributions alimentaires et de qurban, et suivi des madrasas."
      },
      ru: {
        name: "Бурунди",
        summary:
          "Бурунди — наша штаб-квартира в Бужумбуре и база крупнейших полевых операций: колодцы, продовольствие, курбан и мониторинг медресе."
      },
      ar: {
        name: "بوروندي",
        summary:
          "بوروندي هي مقرنا في بوجومبورا وقاعدة أكبر عملياتنا الميدانية، وتشمل آبار المياه وتوزيع الغذاء والأضاحي ومتابعة المدارس الدينية."
      }
    }
  },
];

const baseVideoNote: Record<SupportedLocale, string> = {
  tr: "Video bağlantıları yakında eklenecek.",
  en: "Video links will be added soon.",
  fr: "Les liens vidéo seront ajoutés prochainement.",
  ar: "سيتم إضافة روابط الفيديو قريباً.",
  ru: "Ссылки на видео будут добавлены позже."
};

const serviceDefinitions: ServiceDefinition[] = [
  {
    id: "kurban",
    slug: "kurban",
    countries: [
      { countryId: "chad", videoUrls: [] },
      { countryId: "cameroon", videoUrls: [] },
      { countryId: "yemen", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "KURBAN",
        heroTitle: "Kurban Organizasyonu",
        heroSubtitle:
          "Vekaletlerinizi sahada İslami usullere uygun şekilde yerine getiriyor, kesimden dağıtıma tüm süreci şeffaf raporluyoruz.",
        overview: [
          "Bayram günleri için tedarik ve kesim planını önceden netleştirir, uygun hayvan seçimi ve sağlık kontrollerini sahada doğrularız.",
          "Kesim, parçalama ve dağıtım adımlarını foto/video ve listelerle belgelendirir; bağışçıya teslim raporu sunarız."
        ],
        highlights: [
          "Şer’i uygunluk ve veteriner kontrolleri",
          "Soğuk zincir ve hijyen yönetimi",
          "Belgelendirme ve bağışçı raporu"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "QURBAN",
        heroTitle: "Qurban Operations",
        heroSubtitle:
          "We carry out Qurban mandates in full compliance, documenting every step from slaughter to distribution.",
        overview: [
          "We confirm sourcing and slaughter plans ahead of Eid, verifying animal eligibility and health checks on site.",
          "Slaughter, packing and distribution are documented with lists and photo/video evidence for donor reporting."
        ],
        highlights: [
          "Sharia compliance and vet checks",
          "Cold-chain and hygiene control",
          "Verified donor reporting"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "QURBAN",
        heroTitle: "Organisation du Qurban",
        heroSubtitle:
          "Nous accomplissons vos Qurban conformément aux règles, avec un suivi transparent du sacrifice à la distribution.",
        overview: [
          "Nous validons l'approvisionnement et le plan d'abattage avant l'Aïd, avec contrôle de l'éligibilité et de la santé des animaux.",
          "Abattage, découpe et distribution sont documentés avec listes et preuves photo/vidéo pour les donateurs."
        ],
        highlights: [
          "Conformité religieuse et contrôle vétérinaire",
          "Chaîne du froid et hygiène",
          "Reporting documenté"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "КУРБАН",
        heroTitle: "Операции по курбану",
        heroSubtitle:
          "Мы выполняем курбан в полном соответствии, документируя каждый этап от забоя до распределения.",
        overview: [
          "Мы заранее подтверждаем поставки и план забоя на Курбан, проверяем соответствие и здоровье животных на месте.",
          "Забой, разделка и распределение фиксируются списками и фото/видео для отчетности доноров."
        ],
        highlights: [
          "Шариатское соответствие и ветеринарный контроль",
          "Холодовая цепь и гигиена",
          "Подтвержденная отчетность"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "الأضاحي",
        heroTitle: "تنظيم الأضاحي",
        heroSubtitle:
          "ننفذ الأضاحي وفق الأحكام الشرعية مع توثيق شفاف لكل خطوة من الذبح حتى التوزيع.",
        overview: [
          "نؤكد خطط التوريد والذبح قبل العيد ونتحقق ميدانياً من سلامة الأضاحي وصلاحيتها.",
          "نوثق الذبح والتقطيع والتوزيع بالقوائم والصور والفيديو لإعداد تقارير للمتبرعين."
        ],
        highlights: [
          "التزام شرعي وفحوصات بيطرية",
          "سلسلة تبريد ونظافة",
          "تقارير موثقة للمتبرعين"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "iftar-sicak-yemek",
    slug: "iftar-sicak-yemek",
    countries: [
      { countryId: "gaza", videoUrls: [] },
      { countryId: "yemen", videoUrls: [] },
      { countryId: "bangladesh", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "İFTAR / SICAK YEMEK",
        heroTitle: "İftar ve Sıcak Yemek Dağıtımları",
        heroSubtitle:
          "İftar sofralarından günlük sıcak yemek dağıtımlarına kadar güvenli ve düzenli hizmet sağlıyoruz.",
        overview: [
          "Yerel mutfaklarla çalışır, hijyen ve kalite kontrol uygular, menüleri bölgenin alışkanlıklarına göre planlarız.",
          "Dağıtım noktalarında kuyruk ve kayıt yönetimi yapar; görsel ve yazılı raporlarla şeffaflık sağlarız."
        ],
        highlights: [
          "Yerel mutfak iş birliği",
          "Hijyen ve porsiyon kontrolü",
          "Kayıtlı ve düzenli dağıtım"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "IFTAR / HOT MEAL",
        heroTitle: "Iftar & Hot Meal Support",
        heroSubtitle:
          "From Ramadan iftars to daily feeding programmes, we deliver hot meals safely and consistently.",
        overview: [
          "We work with local kitchens, apply hygiene and quality checks, and tailor menus to local preferences.",
          "Queue management and registration keep distributions orderly, with visual and written reports."
        ],
        highlights: [
          "Local kitchen partnerships",
          "Hygiene and portion control",
          "Recorded, orderly delivery"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "IFTAR / REPAS CHAUD",
        heroTitle: "Distribution de repas chauds et d'iftars",
        heroSubtitle:
          "Des iftars de Ramadan aux repas quotidiens, nous assurons un service sûr et régulier.",
        overview: [
          "Nous travaillons avec des cuisines locales, appliquons des contrôles d'hygiène et adaptons les menus aux habitudes.",
          "La gestion des files et des registres garantit l'ordre, avec des rapports visuels et écrits."
        ],
        highlights: [
          "Partenariats de cuisine locale",
          "Hygiène et contrôle des portions",
          "Distribution enregistrée"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "ИФТАР / ГОРЯЧЕЕ ПИТАНИЕ",
        heroTitle: "Ифтар и горячее питание",
        heroSubtitle:
          "От рамаданских ифтаров до ежедневного питания — мы обеспечиваем безопасную и стабильную раздачу.",
        overview: [
          "Мы работаем с местными кухнями, соблюдаем гигиену и подстраиваем меню под местные привычки.",
          "Управление очередями и регистрацией обеспечивает порядок, с визуальными и письменными отчетами."
        ],
        highlights: [
          "Партнерство с местными кухнями",
          "Гигиена и контроль порций",
          "Зарегистрированная раздача"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "إفطار / وجبة ساخنة",
        heroTitle: "دعم الإفطار والوجبات الساخنة",
        heroSubtitle:
          "من موائد رمضان إلى برامج الإطعام اليومية، نضمن وصول الوجبات الساخنة بأمان وانتظام.",
        overview: [
          "نعمل مع المطابخ المحلية ونطبق معايير النظافة والجودة ونكيّف القوائم مع العادات الغذائية.",
          "نظّم الطوابير والتسجيل يضمنان التوزيع المنتظم مع تقارير مرئية ومكتوبة."
        ],
        highlights: [
          "شراكات مع مطابخ محلية",
          "نظافة وضبط الحصص",
          "توزيع منظم ومسجل"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "su-kuyusu",
    slug: "su-kuyusu",
    countries: [
      { countryId: "chad", videoUrls: [] },
      { countryId: "cameroon", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "SU KUYUSU",
        heroTitle: "Su Kuyusu Projeleri",
        heroSubtitle:
          "Jeolojik etüt, doğru ekipman ve bakım eğitimiyle sürdürülebilir su erişimi sağlıyoruz.",
        overview: [
          "Saha keşfi ve hidrojeolojik analizle en uygun noktayı seçer, toplumun erişimini gözetiriz.",
          "Kurulum sonrası komite ve bakım eğitimleri verir, su güvenliği ve kullanım takibini destekleriz."
        ],
        highlights: [
          "Hidrojeolojik analiz",
          "Dayanıklı altyapı",
          "Yerel bakım eğitimi"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "WATER WELL",
        heroTitle: "Water Well Projects",
        heroSubtitle:
          "Hydrogeological surveys, the right hardware and maintenance training keep wells sustainable.",
        overview: [
          "We assess sites with hydrogeological surveys and select locations that maximize safe community access.",
          "After installation, we train local committees and support water safety and usage monitoring."
        ],
        highlights: [
          "Hydrogeological assessment",
          "Durable infrastructure",
          "Local maintenance training"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "PUITS D'EAU",
        heroTitle: "Projets de puits d'eau",
        heroSubtitle:
          "Études hydrogéologiques, matériel adapté et formation locale pour un accès durable à l'eau.",
        overview: [
          "Nous évaluons les sites par études hydrogéologiques et choisissons des emplacements favorisant l'accès communautaire.",
          "Après l'installation, nous formons des comités locaux et suivons la sécurité et l'usage de l'eau."
        ],
        highlights: [
          "Analyse hydrogéologique",
          "Infrastructures durables",
          "Formation à la maintenance"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "ВОДНЫЕ СКВАЖИНЫ",
        heroTitle: "Проекты скважин",
        heroSubtitle:
          "Гидрогеологические исследования, подходящее оборудование и обучение обеспечивают устойчивый доступ к воде.",
        overview: [
          "Мы оцениваем площадки с помощью гидрогеологических исследований и выбираем точки с безопасным доступом для сообщества.",
          "После установки обучаем местные комитеты и поддерживаем контроль качества и использования воды."
        ],
        highlights: [
          "Гидрогеологическая оценка",
          "Надежная инфраструктура",
          "Обучение обслуживанию"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "حفر الآبار",
        heroTitle: "مشاريع حفر الآبار",
        heroSubtitle:
          "نؤمّن وصولاً مستداماً للمياه عبر الدراسات الهيدروجيولوجية واختيار المعدات المناسبة وتدريب المجتمع.",
        overview: [
          "نقيّم المواقع بالدراسات الهيدروجيولوجية ونختار نقاطاً تعزز الوصول الآمن للمجتمع.",
          "بعد التركيب ندرب اللجان المحلية ونتابع سلامة المياه واستخدامها." 
        ],
        highlights: [
          "تقييم هيدروجيولوجي",
          "بنية تحتية متينة",
          "تدريب على الصيانة"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "gida-dagitimi",
    slug: "gida-dagitimi",
    countries: [
      { countryId: "yemen", videoUrls: [] },
      { countryId: "gaza", videoUrls: [] },
      { countryId: "afghanistan", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "GIDA DAĞITIMI",
        heroTitle: "Gıda Paketi Operasyonları",
        heroSubtitle:
          "İhtiyaç analizine dayalı gıda paketlerini kayıtlı ve kanıtlı şekilde ulaştırıyoruz.",
        overview: [
          "Yerel kurumlarla hane listeleri oluşturur, içerikleri bölgesel beslenme alışkanlıklarına göre belirleriz.",
          "Dağıtımda imza/teslim kayıtları ve görsel kanıt toplar, raporları paylaşırız."
        ],
        highlights: [
          "İhtiyaç temelli hedefleme",
          "Şeffaf teslim kayıtları",
          "Besleyici paket içeriği"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "FOOD DISTRIBUTION",
        heroTitle: "Food Parcel Operations",
        heroSubtitle:
          "We deliver food parcels based on needs assessments with recorded, verified distributions.",
        overview: [
          "Beneficiary lists are prepared with local authorities and parcel contents match local dietary habits.",
          "We collect signed delivery records and visual evidence and share the reports."
        ],
        highlights: [
          "Needs-based targeting",
          "Transparent delivery records",
          "Nutritious parcel content"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION ALIMENTAIRE",
        heroTitle: "Opérations de colis alimentaires",
        heroSubtitle:
          "Nous distribuons des colis alimentaires selon les besoins, avec des livraisons enregistrées et vérifiées.",
        overview: [
          "Les listes de bénéficiaires sont établies avec les autorités locales et les colis respectent les habitudes alimentaires.",
          "Nous collectons les reçus signés et les preuves visuelles puis partageons les rapports."
        ],
        highlights: [
          "Ciblage selon les besoins",
          "Traçabilité des livraisons",
          "Contenu nutritionnel"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "РАСПРЕДЕЛЕНИЕ ПРОДОВОЛЬСТВИЯ",
        heroTitle: "Операции с продуктовыми наборами",
        heroSubtitle:
          "Мы доставляем продуктовые наборы на основе оценки потребностей с подтвержденной выдачей.",
        overview: [
          "Списки бенефициаров составляются с местными властями, а содержимое учитывает пищевые привычки региона.",
          "Мы собираем подписанные ведомости и визуальные доказательства и делимся отчетами."
        ],
        highlights: [
          "Таргетирование по потребностям",
          "Прозрачные записи выдачи",
          "Питательный состав"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع الغذاء",
        heroTitle: "عمليات توزيع السلال الغذائية",
        heroSubtitle:
          "نوزع السلال الغذائية وفق تقييم الاحتياجات مع توثيق وتسليم مُثبت.",
        overview: [
          "نعد قوائم المستفيدين مع الجهات المحلية ونضبط محتوى السلال وفق العادات الغذائية.",
          "نجمع سجلات التسليم الموقعة والأدلة المرئية ونشارك التقارير." 
        ],
        highlights: [
          "استهداف قائم على الاحتياج",
          "سجلات تسليم شفافة",
          "محتوى غذائي مُغذٍ"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "ekmek-dagitimi",
    slug: "ekmek-dagitimi",
    countries: [
      { countryId: "gaza", videoUrls: [] },
      { countryId: "yemen", videoUrls: [] },
      { countryId: "afghanistan", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "EKMEK DAĞITIMI",
        heroTitle: "Ekmek Fırını Destekleri",
        heroSubtitle:
          "Kriz bölgelerinde günlük ekmek ihtiyacını güvenli tedarik ve denetimle karşılıyoruz.",
        overview: [
          "Yerel fırınlarla kapasite planı yapar, un tedarikini ve hijyen standartlarını denetleriz.",
          "Dağıtım noktalarında düzenli akış ve kayıt yönetimi sağlayarak adil erişimi koruruz."
        ],
        highlights: [
          "Günlük üretim planı",
          "Hijyen ve kalite denetimi",
          "Adil erişim yönetimi"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "BREAD DISTRIBUTION",
        heroTitle: "Bakery Support Programmes",
        heroSubtitle:
          "We sustain daily bread supply in crisis zones with safe sourcing and oversight.",
        overview: [
          "We plan bakery capacity, secure flour supply and monitor hygiene and production quality.",
          "Queue and registration management keeps access fair and distributions traceable."
        ],
        highlights: [
          "Daily production planning",
          "Hygiene and quality checks",
          "Fair access management"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION DE PAIN",
        heroTitle: "Soutien aux boulangeries",
        heroSubtitle:
          "Nous assurons le pain quotidien en zone de crise grâce à un approvisionnement sûr et un suivi rigoureux.",
        overview: [
          "Nous planifions la capacité des boulangeries, sécurisons la farine et contrôlons l'hygiène et la qualité.",
          "La gestion des files et des registres garantit un accès équitable et traçable." 
        ],
        highlights: [
          "Planification quotidienne",
          "Contrôles d'hygiène et qualité",
          "Accès équitable"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "РАСПРЕДЕЛЕНИЕ ХЛЕБА",
        heroTitle: "Поддержка пекарен",
        heroSubtitle:
          "Мы обеспечиваем ежедневный хлеб в кризисных районах через безопасные поставки и контроль.",
        overview: [
          "Мы планируем мощность пекарен, обеспечиваем поставки муки и контролируем гигиену и качество.",
          "Управление очередями и регистрацией гарантирует справедливый и отслеживаемый доступ."
        ],
        highlights: [
          "Ежедневное планирование",
          "Контроль гигиены и качества",
          "Справедливый доступ"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع الخبز",
        heroTitle: "دعم المخابز",
        heroSubtitle:
          "نؤمّن الخبز اليومي في مناطق الأزمات عبر توريد آمن ومتابعة دقيقة.",
        overview: [
          "نخطط لقدرة المخابز ونؤمّن الدقيق ونراقب النظافة وجودة الإنتاج.",
          "إدارة الطوابير والتسجيل تحفظ العدالة وقابلية التتبع." 
        ],
        highlights: [
          "تخطيط إنتاج يومي",
          "رقابة صحية وجودة",
          "إدارة وصول عادلة"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "kuran-dagitimi",
    slug: "kuran-dagitimi",
    countries: [
      { countryId: "chad", videoUrls: [] },
      { countryId: "afghanistan", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "KUR'AN DAĞITIMI",
        heroTitle: "Kur'an-ı Kerim Dağıtımları",
        heroSubtitle:
          "İhtiyaç sahibi eğitim kurumları ve camilere kaliteli mushaflar ulaştırıyoruz.",
        overview: [
          "Yerel kanaat önderleriyle ihtiyaç listelerini doğrular, dayanıklı baskılar tercih ederiz.",
          "Dağıtımı kayıt altına alır; temel kıraat destekleriyle öğrenmeyi güçlendiririz."
        ],
        highlights: [
          "Doğrulanmış ihtiyaç listeleri",
          "Kaliteli ve dayanıklı baskı",
          "Eğitim destekleri"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "QUR'AN DISTRIBUTION",
        heroTitle: "Qur'an Distribution",
        heroSubtitle:
          "We deliver high-quality Qur'ans to schools and mosques with verified needs.",
        overview: [
          "We confirm need lists with local scholars and choose durable, easy-to-read copies.",
          "Distributions are recorded and supported with basic recitation guidance."
        ],
        highlights: [
          "Verified needs lists",
          "Durable quality printing",
          "Learning support"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION DU CORAN",
        heroTitle: "Distribution du Coran",
        heroSubtitle:
          "Nous livrons des Corans de qualité aux écoles et mosquées selon des besoins vérifiés.",
        overview: [
          "Nous confirmons les listes de besoins avec les érudits locaux et choisissons des exemplaires durables.",
          "La distribution est enregistrée et accompagnée d'un soutien à la récitation."
        ],
        highlights: [
          "Besoins vérifiés",
          "Impression durable",
          "Appui à l'apprentissage"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "РАСПРЕДЕЛЕНИЕ КОРАНА",
        heroTitle: "Распределение Корана",
        heroSubtitle:
          "Мы доставляем качественные экземпляры Корана в школы и мечети по подтвержденным потребностям.",
        overview: [
          "Мы подтверждаем списки потребностей с местными учеными и выбираем долговечные издания.",
          "Раздача фиксируется и сопровождается базовой поддержкой по чтению."
        ],
        highlights: [
          "Подтвержденные списки потребностей",
          "Надежная печать",
          "Поддержка обучения"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع المصاحف",
        heroTitle: "توزيع القرآن الكريم",
        heroSubtitle:
          "نوزع مصاحف عالية الجودة على المدارس والمساجد بناءً على احتياجات موثقة.",
        overview: [
          "نؤكد قوائم الاحتياج مع العلماء المحليين ونختار نسخاً متينة وسهلة القراءة.",
          "نوثق التوزيع ونقدم دعماً أساسياً للتلاوة." 
        ],
        highlights: [
          "قوائم احتياج موثقة",
          "طباعة متينة",
          "دعم التعلم"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "elbise-dagitimi",
    slug: "elbise-dagitimi",
    countries: [
      { countryId: "afghanistan", videoUrls: [] },
      { countryId: "yemen", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "ELBİSE DAĞITIMI",
        heroTitle: "Giyim Desteği",
        heroSubtitle:
          "Mevsimsel giyim desteğini mahremiyet ve saygı esaslarıyla ulaştırıyoruz.",
        overview: [
          "Beden ve yaş listelerini önceden toplar, mümkün olduğunca yerel üreticilerden tedarik ederiz.",
          "Randevulu ve düzenli teslimatla kalabalığı önler, onurlu bir dağıtım sağlarız."
        ],
        highlights: [
          "Beden planlaması",
          "Yerel tedarik",
          "Onurlu dağıtım"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "CLOTHING DISTRIBUTION",
        heroTitle: "Clothing Support",
        heroSubtitle:
          "We deliver seasonal clothing with dignity, privacy, and careful sizing.",
        overview: [
          "We collect sizes in advance and source locally whenever possible.",
          "Appointment-based delivery avoids crowding and protects privacy." 
        ],
        highlights: [
          "Sizing in advance",
          "Local sourcing",
          "Dignified distribution"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION DE VÊTEMENTS",
        heroTitle: "Aide vestimentaire",
        heroSubtitle:
          "Nous distribuons des vêtements saisonniers avec dignité, respect et préparation des tailles.",
        overview: [
          "Nous collectons les tailles à l'avance et privilégions l'approvisionnement local.",
          "La distribution sur rendez-vous évite la foule et protège la confidentialité." 
        ],
        highlights: [
          "Tailles préparées",
          "Approvisionnement local",
          "Distribution digne"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "РАСПРЕДЕЛЕНИЕ ОДЕЖДЫ",
        heroTitle: "Поддержка одеждой",
        heroSubtitle:
          "Мы передаем сезонную одежду с уважением, приватностью и точным подбором размеров.",
        overview: [
          "Мы заранее собираем размеры и по возможности закупаем у местных производителей.",
          "Раздача по расписанию предотвращает скопления и сохраняет приватность."
        ],
        highlights: [
          "Подготовка размеров",
          "Местные закупки",
          "Достойная раздача"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع الملابس",
        heroTitle: "دعم الملابس",
        heroSubtitle:
          "نوفر الملابس الموسمية بخصوصية واحترام مع إعداد المقاسات مسبقاً.",
        overview: [
          "نجمع القياسات مسبقاً ونشتري من المنتجين المحليين قدر الإمكان.",
          "التوزيع بالمواعيد يحفظ الخصوصية ويمنع الازدحام." 
        ],
        highlights: [
          "إعداد المقاسات",
          "توريد محلي",
          "توزيع كريم"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "kirtasiye-dagitimi",
    slug: "kirtasiye-dagitimi",
    countries: [
      { countryId: "afghanistan", videoUrls: [] },
      { countryId: "bangladesh", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "KIRTASİYE DAĞITIMI",
        heroTitle: "Kırtasiye ve Eğitim Kitleri",
        heroSubtitle:
          "Okul devamlılığını destekleyen kırtasiye ve hijyen kitleri hazırlıyoruz.",
        overview: [
          "Okul yönetimleriyle sınıf listelerini doğrular, yaşa uygun içerikler planlarız.",
          "Dağıtım sonrası kullanım ve bakım rehberi paylaşır, okul geri bildirimlerini izleriz."
        ],
        highlights: [
          "Okul listeleriyle doğrulama",
          "Yaşa uygun kitler",
          "Eğitim sürekliliği"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "SCHOOL SUPPLIES",
        heroTitle: "School Supply Kits",
        heroSubtitle:
          "We prepare stationery and hygiene kits that help children stay in school.",
        overview: [
          "We validate class lists with school administrators and tailor kits to each age group.",
          "After distribution we share care guidance and monitor school feedback."
        ],
        highlights: [
          "Verified school lists",
          "Age-appropriate kits",
          "Learning continuity"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "FOURNITURES SCOLAIRES",
        heroTitle: "Kits scolaires",
        heroSubtitle:
          "Nous préparons des kits de fournitures et d'hygiène pour soutenir la scolarité.",
        overview: [
          "Nous validons les listes de classes avec les directions et adaptons les kits à chaque âge.",
          "Après distribution, nous partageons des consignes d'entretien et suivons les retours." 
        ],
        highlights: [
          "Listes scolaires vérifiées",
          "Kits adaptés à l'âge",
          "Continuité scolaire"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "КАНЦТОВАРЫ",
        heroTitle: "Школьные наборы",
        heroSubtitle:
          "Мы готовим наборы канцелярии и гигиены, чтобы дети оставались в школе.",
        overview: [
          "Мы подтверждаем списки классов с администрацией и адаптируем наборы по возрасту.",
          "После раздачи передаем рекомендации по уходу и отслеживаем обратную связь."
        ],
        highlights: [
          "Проверенные школьные списки",
          "Наборы по возрасту",
          "Непрерывность обучения"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع القرطاسية",
        heroTitle: "حقائب مدرسية كاملة",
        heroSubtitle:
          "نجهز مجموعات القرطاسية والنظافة لدعم استمرار الأطفال في التعليم.",
        overview: [
          "نراجع قوائم الصفوف مع إدارات المدارس ونصمم المجموعات حسب الفئة العمرية.",
          "نشارك إرشادات العناية ونتابع ملاحظات المدارس بعد التوزيع." 
        ],
        highlights: [
          "قوائم مدرسية موثقة",
          "مجموعات مناسبة للعمر",
          "استمرارية التعلم"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "katarakt-ameliyati",
    slug: "katarakt-ameliyati",
    countries: [
      { countryId: "chad", videoUrls: [] },
      { countryId: "cameroon", videoUrls: [] },
      { countryId: "yemen", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "KATARAKT AMELİYATI",
        heroTitle: "Katarakt Cerrahisi",
        heroSubtitle:
          "Mobil taramalar ve güvenli cerrahi ile görme kaybını azaltıyoruz.",
        overview: [
          "Ön tarama ve sevk süreçlerini planlar, uzman ekiplerle operasyonları gerçekleştiririz.",
          "Ameliyat sonrası kontrol ve ilaç takibini yerel sağlık çalışanlarıyla sürdürürüz."
        ],
        highlights: [
          "Uzman cerrahi ekip",
          "Ücretsiz tarama ve sevk",
          "Ameliyat sonrası takip"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "CATARACT SURGERY",
        heroTitle: "Cataract Campaigns",
        heroSubtitle:
          "Mobile screenings and safe surgery help restore sight for vulnerable communities.",
        overview: [
          "We plan pre-screening and referral pathways, then deliver surgeries with specialist teams.",
          "Post-operative medication and follow-ups are coordinated with local health workers."
        ],
        highlights: [
          "Specialist surgical teams",
          "Free screening and referrals",
          "Post-op follow-up"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "CHIRURGIE DE LA CATARACTE",
        heroTitle: "Campagnes de cataracte",
        heroSubtitle:
          "Des dépistages mobiles et une chirurgie sûre redonnent la vue aux personnes vulnérables.",
        overview: [
          "Nous organisons le dépistage et l'orientation, puis réalisons les interventions avec des équipes spécialisées.",
          "Le suivi post-opératoire et les traitements sont coordonnés avec les soignants locaux." 
        ],
        highlights: [
          "Équipes chirurgicales spécialisées",
          "Dépistage et orientation gratuits",
          "Suivi post-op"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "ОПЕРАЦИИ ПО КАТАРАКТЕ",
        heroTitle: "Кампании по катаракте",
        heroSubtitle:
          "Мобильные обследования и безопасные операции помогают вернуть зрение уязвимым людям.",
        overview: [
          "Мы организуем скрининг и направления, затем проводим операции с профильными командами.",
          "Послеоперационные лекарства и контроль координируются с местными медработниками."
        ],
        highlights: [
          "Специализированные хирургические команды",
          "Бесплатный скрининг и направление",
          "Послеоперационное наблюдение"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "عمليات الماء الأبيض",
        heroTitle: "حملات جراحة الماء الأبيض",
        heroSubtitle:
          "الفحوصات المتنقلة والجراحة الآمنة تساعد على استعادة البصر للفئات الأشد حاجة.",
        overview: [
          "نخطط للفحوصات والإحالة ثم ننفذ العمليات مع فرق متخصصة.",
          "نوفر الأدوية والمتابعة بعد الجراحة بالتعاون مع الكوادر المحلية." 
        ],
        highlights: [
          "فرق جراحية متخصصة",
          "فحوصات وإحالة مجانية",
          "متابعة ما بعد الجراحة"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "cocuk-sunneti",
    slug: "cocuk-sunneti",
    countries: [
      { countryId: "chad", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "ÇOCUK SÜNNETİ",
        heroTitle: "Toplu Sünnet Organizasyonları",
        heroSubtitle:
          "Steril koşullarda toplu sünnet organizasyonları yürütüyor, aileleri bilgilendiriyoruz.",
        overview: [
          "Her çocuk için tek kullanımlık setler hazırlar, tıbbi onam ve bilgilendirme süreçlerini uygularız.",
          "Operasyon sonrası bakım paketleri ve danışmanlıkla güvenli iyileşmeyi destekleriz."
        ],
        highlights: [
          "Steril tıbbi ortam",
          "Bilgilendirilmiş onam",
          "Bakım ve takip"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "CHILD CIRCUMCISION",
        heroTitle: "Mass Circumcision Clinics",
        heroSubtitle:
          "We deliver safe procedures in sterile conditions while preparing families for aftercare.",
        overview: [
          "Each child receives a disposable kit, with informed consent and clear pre-op guidance.",
          "Families receive care packs and access to medical advice for safe recovery." 
        ],
        highlights: [
          "Sterile medical setup",
          "Informed consent",
          "Aftercare support"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "CIRCONCISION DES ENFANTS",
        heroTitle: "Cliniques de circoncision",
        heroSubtitle:
          "Nous réalisons les interventions en conditions stériles et préparons les familles aux soins.",
        overview: [
          "Chaque enfant reçoit un kit à usage unique, avec consentement éclairé et information préalable.",
          "Les familles repartent avec des kits de soins et un suivi médical." 
        ],
        highlights: [
          "Installation stérile",
          "Consentement éclairé",
          "Suivi post-opératoire"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "ДЕТСКОЕ ОБРЕЗАНИЕ",
        heroTitle: "Клиники массового обрезания",
        heroSubtitle:
          "Мы проводим процедуры в стерильных условиях и готовим семьи к послеоперационному уходу.",
        overview: [
          "Каждому ребенку выдается одноразовый набор, с информированным согласием и понятными инструкциями.",
          "Семьи получают наборы ухода и доступ к медицинским консультациям."
        ],
        highlights: [
          "Стерильные медицинские условия",
          "Информированное согласие",
          "Послеоперационный уход"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "ختان الأطفال",
        heroTitle: "عيادات ختان جماعية",
        heroSubtitle:
          "ننفذ العمليات في بيئة معقمة مع إعداد العائلات للرعاية اللاحقة.",
        overview: [
          "يحصل كل طفل على عدة أحادية الاستخدام مع موافقة مستنيرة وإرشادات واضحة قبل العملية.",
          "نقدم للعائلات حقائب العناية واستشارات طبية لضمان التعافي." 
        ],
        highlights: [
          "بيئة طبية معقمة",
          "موافقة مستنيرة",
          "رعاية ما بعد العملية"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "mescid-medrese-insasi",
    slug: "mescid-medrese-insasi",
    countries: [
      { countryId: "chad", videoUrls: [] },
      { countryId: "cameroon", videoUrls: [] },
      { countryId: "afghanistan", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "MESCİD-MEDRESE İNŞASI",
        heroTitle: "Mescid ve Medrese İnşaatları",
        heroSubtitle:
          "Yerel mimariye uygun mescid ve medreselerle kalıcı eğitim ve ibadet alanları oluşturuyoruz.",
        overview: [
          "İhtiyaç tespiti, proje çizimi ve malzeme tedarikini yerel paydaşlarla birlikte planlarız.",
          "Teslim sonrası bakım komiteleri ve kullanım planlarıyla sürdürülebilirliği güvence altına alırız."
        ],
        highlights: [
          "Yerel mimari uyum",
          "Topluluk katılımı",
          "Sürdürülebilir işletim"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "MOSQUE & MADRASA",
        heroTitle: "Mosque and Madrasa Construction",
        heroSubtitle:
          "We build lasting worship and learning spaces aligned with local architecture.",
        overview: [
          "We assess needs, plan designs, and source materials with local stakeholders.",
          "Post-handover we set up maintenance committees and usage plans for sustainability."
        ],
        highlights: [
          "Locally aligned design",
          "Community ownership",
          "Sustainable operations"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "CONSTRUCTION MOSQUÉE/MADRASA",
        heroTitle: "Construction de mosquées et madrasas",
        heroSubtitle:
          "Nous créons des espaces durables de prière et d'éducation adaptés à l'architecture locale.",
        overview: [
          "Nous évaluons les besoins, planifions les plans et approvisionnons avec les parties prenantes locales.",
          "Après livraison, nous mettons en place des comités d'entretien et des plans d'usage durables." 
        ],
        highlights: [
          "Architecture locale",
          "Appropriation communautaire",
          "Exploitation durable"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "СТРОИТЕЛЬСТВО МЕЧЕТЕЙ/МЕДРЕСЕ",
        heroTitle: "Строительство мечетей и медресе",
        heroSubtitle:
          "Мы создаем устойчивые пространства для молитвы и обучения с учетом местной архитектуры.",
        overview: [
          "Мы оцениваем потребности, планируем проект и закупаем материалы вместе с местными стейкхолдерами.",
          "После сдачи формируем комитеты обслуживания и планы использования для устойчивости."
        ],
        highlights: [
          "Локальная архитектура",
          "Участие сообщества",
          "Устойчивая эксплуатация"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "بناء المساجد والمدارس",
        heroTitle: "بناء المساجد والمدارس الشرعية",
        heroSubtitle:
          "ننشئ مساحات عبادة وتعليم دائمة تراعي الطابع المعماري المحلي.",
        overview: [
          "نحدد الاحتياجات ونخطط التصاميم ونوفر المواد بالتعاون مع الشركاء المحليين.",
          "بعد التسليم ننشئ لجان صيانة وخطط تشغيل لضمان الاستدامة." 
        ],
        highlights: [
          "تصميم محلي",
          "ملكية مجتمعية",
          "تشغيل مستدام"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "tankerle-su-dagitimi",
    slug: "tankerle-su-dagitimi",
    countries: [
      { countryId: "yemen", videoUrls: [] },
      { countryId: "gaza", videoUrls: [] },
      { countryId: "afghanistan", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "TANKERLE SU DAĞITIMI",
        heroTitle: "Su Tankeri Operasyonları",
        heroSubtitle:
          "Acil durumlarda tankerle güvenli su ulaştırarak kesintisiz erişim sağlıyoruz.",
        overview: [
          "Güzergâh ve dağıtım planlarını güvenlik ve ihtiyaç önceliğine göre oluştururuz.",
          "Su kalitesi kontrolü ve kayıtlı teslimatla hijyen ve hesap verebilirliği koruruz."
        ],
        highlights: [
          "Hızlı acil müdahale",
          "Su kalitesi takibi",
          "Kayıtlı dağıtım"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "WATER BY TANKER",
        heroTitle: "Water Tanker Operations",
        heroSubtitle:
          "We deliver safe water by tanker during emergencies to keep access uninterrupted.",
        overview: [
          "Routes and distribution plans follow security and priority needs assessments.",
          "Water quality checks and recorded deliveries ensure hygiene and accountability."
        ],
        highlights: [
          "Rapid emergency response",
          "Water quality monitoring",
          "Recorded deliveries"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION PAR CAMION-CITERNE",
        heroTitle: "Opérations de camions-citernes",
        heroSubtitle:
          "Nous livrons de l'eau sûre par camions-citernes en situation d'urgence.",
        overview: [
          "Les itinéraires et la distribution suivent les priorités et la sécurité.",
          "Les contrôles de qualité et les livraisons enregistrées garantissent l'hygiène et la redevabilité." 
        ],
        highlights: [
          "Réponse d'urgence rapide",
          "Suivi de la qualité de l'eau",
          "Livraisons enregistrées"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "ВОДА ЦИСТЕРНАМИ",
        heroTitle: "Операции автоцистернами",
        heroSubtitle:
          "В экстренных ситуациях мы доставляем безопасную воду автоцистернами, обеспечивая непрерывный доступ.",
        overview: [
          "Маршруты и раздача строятся по приоритетам и требованиям безопасности.",
          "Контроль качества воды и подтвержденные выдачи обеспечивают гигиену и подотчетность."
        ],
        highlights: [
          "Быстрая экстренная реакция",
          "Мониторинг качества воды",
          "Подтвержденные выдачи"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع المياه بالصهاريج",
        heroTitle: "عمليات صهاريج المياه",
        heroSubtitle:
          "نؤمن مياهًا آمنة بالصهاريج في الطوارئ لضمان استمرار الوصول.",
        overview: [
          "نخطط المسارات والتوزيع وفق الأولويات ومتطلبات الأمن.",
          "نراقب جودة المياه ونوثق التسليم للحفاظ على النظافة والمساءلة." 
        ],
        highlights: [
          "استجابة طارئة سريعة",
          "مراقبة جودة المياه",
          "تسليم موثق"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "un-dagitimi",
    slug: "un-dagitimi",
    countries: [
      { countryId: "gaza", videoUrls: [] },
      { countryId: "yemen", videoUrls: [] }
    ],
    translations: {
      tr: {
        name: "UN DAĞITIMI",
        heroTitle: "Un Dağıtımları",
        heroSubtitle:
          "Un desteklerini yerel ekmek üretimini sürdürecek şekilde planlıyoruz.",
        overview: [
          "Fırın kapasitesi ve hane ihtiyacını analiz eder, tedariki güvenli şekilde organize ederiz.",
          "Dağıtım ve üretim takibini yapar, raporları görsel kanıtlarla sunarız."
        ],
        highlights: [
          "Kapasiteye göre planlama",
          "Güvenli tedarik zinciri",
          "Şeffaf raporlama"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "FLOUR DISTRIBUTION",
        heroTitle: "Flour Support",
        heroSubtitle:
          "We plan flour support to keep local bread production running.",
        overview: [
          "We assess bakery capacity and household demand, then organize secure supply.",
          "We track distribution and production outcomes with visual reporting."
        ],
        highlights: [
          "Capacity-based planning",
          "Secure supply chain",
          "Transparent reporting"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION DE FARINE",
        heroTitle: "Soutien en farine",
        heroSubtitle:
          "Nous planifions la farine pour maintenir la production locale de pain.",
        overview: [
          "Nous évaluons la capacité des boulangeries et la demande des ménages puis sécurisons l'approvisionnement.",
          "Nous suivons la distribution et la production avec des preuves visuelles." 
        ],
        highlights: [
          "Planification selon la capacité",
          "Chaîne d'approvisionnement sécurisée",
          "Reporting transparent"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "РАСПРЕДЕЛЕНИЕ МУКИ",
        heroTitle: "Поддержка мукой",
        heroSubtitle:
          "Мы планируем поддержку мукой, чтобы сохранять местное производство хлеба.",
        overview: [
          "Мы оцениваем мощность пекарен и спрос домохозяйств, затем обеспечиваем надежные поставки.",
          "Мы отслеживаем раздачу и производство с визуальной отчетностью."
        ],
        highlights: [
          "Планирование по мощности",
          "Надежная цепочка поставок",
          "Прозрачная отчетность"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع الطحين",
        heroTitle: "دعم الطحين",
        heroSubtitle:
          "نخطط دعم الطحين لضمان استمرار إنتاج الخبز محلياً.",
        overview: [
          "نقيّم قدرة المخابز واحتياج الأسر ثم نؤمّن سلسلة التوريد.",
          "نتابع التوزيع والإنتاج بتقارير موثقة مرئية." 
        ],
        highlights: [
          "تخطيط حسب القدرة",
          "سلسلة توريد آمنة",
          "تقارير شفافة"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "sut-kecisi-dagitimi",
    slug: "sut-kecisi-dagitimi",
    countries: [
      { countryId: "chad", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "SÜT KEÇİSİ DAĞITIMI",
        heroTitle: "Süt Keçisi Programları",
        heroSubtitle:
          "Süt keçisi destekleriyle ailelerin gelir ve beslenme dayanıklılığını artırıyoruz.",
        overview: [
          "Uygun haneleri tespit eder, hayvan sağlığı kontrolleri ve teslimat kayıtlarıyla süreci yönetiriz.",
          "Bakım, yem ve üretim takibi konusunda eğitim vererek sürdürülebilirliği güçlendiririz."
        ],
        highlights: [
          "Hedefli hane seçimi",
          "Veteriner kontrolü",
          "Bakım ve üretim eğitimi"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "DAIRY GOAT DISTRIBUTION",
        heroTitle: "Dairy Goat Livelihoods",
        heroSubtitle:
          "We provide dairy goats to strengthen household nutrition and income resilience.",
        overview: [
          "We identify eligible households and manage deliveries with health checks and documentation.",
          "Training on care, feed, and production helps sustain long-term outcomes."
        ],
        highlights: [
          "Targeted household selection",
          "Veterinary checks",
          "Care and production training"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION DE CHÈVRES LAITIÈRES",
        heroTitle: "Programme chèvres laitières",
        heroSubtitle:
          "Nous distribuons des chèvres laitières pour renforcer nutrition et revenus des familles.",
        overview: [
          "Nous identifions les ménages éligibles et gérons les livraisons avec contrôles vétérinaires et documents.",
          "La formation à l'entretien, l'alimentation et la production assure la durabilité." 
        ],
        highlights: [
          "Sélection des ménages",
          "Contrôle vétérinaire",
          "Formation à l'entretien"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "РАСПРЕДЕЛЕНИЕ МОЛОЧНЫХ КОЗ",
        heroTitle: "Программа молочных коз",
        heroSubtitle:
          "Мы распределяем молочных коз для укрепления питания и устойчивого дохода семей.",
        overview: [
          "Мы отбираем подходящие домохозяйства и ведем выдачу с ветеринарными проверками и документацией.",
          "Обучение уходу, кормлению и производству помогает обеспечить устойчивость."
        ],
        highlights: [
          "Целевой отбор домохозяйств",
          "Ветеринарный контроль",
          "Обучение уходу и производству"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع الماعز الحلوب",
        heroTitle: "برامج الماعز الحلوب",
        heroSubtitle:
          "نوزع ماعز الحليب لتعزيز التغذية والقدرة على كسب الدخل.",
        overview: [
          "نحدد الأسر المستحقة وندير التسليم مع الفحوصات البيطرية والتوثيق.",
          "نقدم تدريباً على الرعاية والعلف والإنتاج لتعزيز الاستدامة." 
        ],
        highlights: [
          "اختيار الأسر المستهدفة",
          "فحوصات بيطرية",
          "تدريب على الرعاية والإنتاج"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "battaniye-dagitimi",
    slug: "battaniye-dagitimi",
    countries: [
      { countryId: "gaza", videoUrls: [] },
      { countryId: "yemen", videoUrls: [] },
      { countryId: "afghanistan", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "BATTANİYE DAĞITIMI",
        heroTitle: "Kışlık Battaniye Destekleri",
        heroSubtitle:
          "Kış döneminde en kırılgan hanelere hızlı ve onurlu battaniye desteği sağlıyoruz.",
        overview: [
          "Soğuk hava riski ve hane kırılganlığına göre önceliklendirme yaparız.",
          "Dağıtım kayıtları ve görsel kanıtla şeffaflık sağlarız."
        ],
        highlights: [
          "Kışa hazırlık",
          "Kırılganlık temelli hedefleme",
          "Şeffaf kayıt"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "BLANKET DISTRIBUTION",
        heroTitle: "Winter Blanket Aid",
        heroSubtitle:
          "We provide rapid, dignified blanket support for the most vulnerable households.",
        overview: [
          "We prioritise based on winter risk and household vulnerability.",
          "Distribution records and visual evidence keep delivery transparent." 
        ],
        highlights: [
          "Winter readiness",
          "Vulnerability-based targeting",
          "Transparent records"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION DE COUVERTURES",
        heroTitle: "Aide en couvertures",
        heroSubtitle:
          "Nous fournissons rapidement des couvertures aux ménages les plus vulnérables.",
        overview: [
          "La priorisation se fait selon le risque hivernal et la vulnérabilité des ménages.",
          "Les registres de distribution et les preuves visuelles assurent la transparence." 
        ],
        highlights: [
          "Préparation à l'hiver",
          "Ciblage selon la vulnérabilité",
          "Registres transparents"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "РАСПРЕДЕЛЕНИЕ ОДЕЯЛ",
        heroTitle: "Зимняя поддержка одеялами",
        heroSubtitle:
          "Мы быстро и достойно обеспечиваем одеялами самые уязвимые семьи.",
        overview: [
          "Приоритизация строится по зимнему риску и уязвимости домохозяйств.",
          "Реестры выдачи и визуальные доказательства обеспечивают прозрачность."
        ],
        highlights: [
          "Подготовка к зиме",
          "Таргетирование по уязвимости",
          "Прозрачные записи"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع البطانيات",
        heroTitle: "دعم البطانيات الشتوية",
        heroSubtitle:
          "نقدم دعماً سريعاً وكريماً بالبطانيات للأسر الأشد هشاشة.",
        overview: [
          "نحدد الأولويات حسب مخاطر الشتاء وهشاشة الأسر.",
          "نوثق التوزيع بسجلات وأدلة مرئية لضمان الشفافية." 
        ],
        highlights: [
          "الاستعداد للشتاء",
          "استهداف حسب الهشاشة",
          "سجلات شفافة"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  },
  {
    id: "cadir-dagitimi",
    slug: "cadir-dagitimi",
    countries: [
      { countryId: "yemen", videoUrls: [] },
      { countryId: "gaza", videoUrls: [] },
      { countryId: "afghanistan", videoUrls: [] },
    ],
    translations: {
      tr: {
        name: "ÇADIR DAĞITIMI",
        heroTitle: "Acil Barınma Çözümleri",
        heroSubtitle:
          "Acil barınma ihtiyacında güvenli ve hızlı çadır kurulumları yapıyoruz.",
        overview: [
          "Yerleşim alanı seçimi ve güvenlik değerlendirmesiyle kurulum planları hazırlarız.",
          "Kurulum sonrası kullanım ve bakım rehberi sunar, ihtiyaç takibini sürdürürüz."
        ],
        highlights: [
          "Güvenli alan planlaması",
          "Hızlı kurulum",
          "Kullanım ve bakım desteği"
        ],
        videoNote: baseVideoNote.tr
      },
      en: {
        name: "SHELTER TENTS",
        heroTitle: "Emergency Shelter Solutions",
        heroSubtitle:
          "We deliver safe, rapid shelter solutions for displacement and disaster response.",
        overview: [
          "We assess sites and safety conditions before setting up shelter layouts.",
          "After installation we share care guidance and keep tracking needs."
        ],
        highlights: [
          "Safe site planning",
          "Rapid deployment",
          "Use and care support"
        ],
        videoNote: baseVideoNote.en
      },
      fr: {
        name: "DISTRIBUTION DE TENTES",
        heroTitle: "Solutions d'abri d'urgence",
        heroSubtitle:
          "Nous fournissons des abris sûrs et rapides pour les situations de crise.",
        overview: [
          "Nous évaluons les sites et la sécurité avant de planifier l'installation.",
          "Après montage, nous partageons les consignes d'entretien et suivons les besoins." 
        ],
        highlights: [
          "Planification sécurisée",
          "Déploiement rapide",
          "Support d'utilisation"
        ],
        videoNote: baseVideoNote.fr
      },
      ru: {
        name: "РАСПРЕДЕЛЕНИЕ ПАЛАТОК",
        heroTitle: "Экстренные решения для укрытия",
        heroSubtitle:
          "Мы предоставляем безопасные и быстрые решения для размещения при ЧС и перемещении.",
        overview: [
          "Мы оцениваем площадки и условия безопасности перед планированием установки.",
          "После установки передаем инструкции по использованию и продолжаем мониторинг потребностей."
        ],
        highlights: [
          "Безопасное планирование площадок",
          "Быстрое развертывание",
          "Поддержка использования и ухода"
        ],
        videoNote: baseVideoNote.ru
      },
      ar: {
        name: "توزيع الخيام",
        heroTitle: "حلول الإيواء الطارئة",
        heroSubtitle:
          "نوفر حلول إيواء آمنة وسريعة لحالات الطوارئ والنزوح.",
        overview: [
          "نقيم المواقع ومعايير السلامة قبل تخطيط تركيب المأوى.",
          "بعد التركيب نقدم إرشادات الاستخدام ونواصل متابعة الاحتياجات." 
        ],
        highlights: [
          "تخطيط مواقع آمن",
          "تركيب سريع",
          "دعم الاستخدام والصيانة"
        ],
        videoNote: baseVideoNote.ar
      }
    }
  }
];

export const SERVICES = serviceDefinitions;

export const COUNTRIES = countryDetails;

export const getServiceBySlug = (slug: string) =>
  SERVICES.find((service) => service.slug === slug);

export const getServiceTranslation = (service: ServiceDefinition, locale: SupportedLocale) =>
  service.translations[locale] ?? service.translations.en;

export const getCountryContent = (countryId: CountryId, locale: SupportedLocale) => {
  const country = COUNTRIES.find((c) => c.id === countryId);
  if (!country) return null;
  return country.translations[locale] ?? country.translations.en;
};

export const resolveLocale = (locale: string): SupportedLocale => {
  const normalized = locale.split("-")[0] as SupportedLocale;
  return (['tr','en','fr','ar','ru'] as SupportedLocale[]).includes(normalized)
    ? normalized
    : 'en';
};
